import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Product, ProductVariant } from '../../../lib/api/models';
import { ProductImage } from '../../../components/ui/OptimizedImage';
import { useAuth } from '../../../context/AuthContext';

interface ProductCardProps {
  product: Product;
  index: number;
  onProductClick: (product: Product, variant: ProductVariant | null, position: number) => void;
}

const ProductCard = ({ product, index, onProductClick }: ProductCardProps) => {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const isAuthenticated = auth.isAuthenticated;
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(
    product.variants && product.variants.length > 0 ? product.variants[0] : null
  );
  const [isHovered, setIsHovered] = useState(false);

  // Obtener URL de imagen
  const getImageUrl = (product: Product): string => {
    if (!product.images || product.images.length === 0) {
      return '/placeholder-product.jpg';
    }
    return product.images[0].url;
  };

  // Obtener precio actual (del producto base o de la variante seleccionada)
  const getCurrentPrice = (): number => {
    if (selectedVariant && selectedVariant.price > 0) {
      return selectedVariant.price;
    }
    return product.price;
  };

  // Obtener precio original (antes de promoción) si hay promoción activa
  const getCurrentOriginalPrice = (): number | undefined => {
    if (selectedVariant) {
      // Usar el nuevo campo originalPrice de la variante
      return (selectedVariant as any).originalPrice;
    }
    return (product as any).originalPrice;
  };

  // Verificar si hay promoción activa
  const getHasPromo = (): boolean => {
    if (selectedVariant) {
      return (selectedVariant as any).hasPromo === true;
    }
    return (product as any).hasPromo === true;
  };

  // Obtener stock actual
  const getCurrentStock = (): number => {
    if (selectedVariant) {
      return selectedVariant.stock;
    }
    return product.stock;
  };

  const currentPrice = getCurrentPrice();
  const currentOriginalPrice = getCurrentOriginalPrice();
  const currentStock = getCurrentStock();
  const hasVariants = product.variants && product.variants.length > 0;
  const hasDiscount = getHasPromo() && currentOriginalPrice && currentOriginalPrice > currentPrice;
  const hasSpecialPromotion = Boolean(product.promotionMessage?.trim());

  // Calcular descuento
  const discountPercentage = hasDiscount
    ? Math.round((1 - currentPrice / currentOriginalPrice!) * 100)
    : 0;

  return (
    <motion.div
      className={`group relative bg-white border-2 rounded-lg hover:shadow-xl transition-all duration-300 overflow-hidden ${hasSpecialPromotion ? 'border-[#f6cf02]' : 'border-gray-200'}`}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid="product-card"
    >
      {hasSpecialPromotion && (
        <div className="absolute left-1/2 top-0 z-20 -translate-x-1/2 rounded-b-md bg-[#f6cf02] px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-black shadow-sm">
          Promoción especial
        </div>
      )}

      <Link
        to={`/products/${product.id}`}
        className="block"
        onClick={() => onProductClick(product, selectedVariant, index + 1)}
      >
        {/* Imagen del producto */}
        <div className="relative aspect-square bg-white p-4 overflow-hidden">
          {/* Efecto de fondo decorativo */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3/4 h-3/4 bg-gradient-radial from-black/[0.03] to-transparent opacity-50" />

          {/* Badges superiores */}
          <div className="absolute top-2 left-2 right-2 flex justify-between items-start z-10">
            {/* Badge de descuento */}
            {hasDiscount ? (
              <div className="bg-red-600 text-white text-xs font-bold px-3 py-1.5 rounded-md shadow-lg">
                -{discountPercentage}%
              </div>
            ) : <div />}

            {/* Badge de stock bajo */}
            {currentStock > 0 && currentStock <= 5 ? (
              <div className="bg-orange-500 text-white text-xs font-semibold px-3 py-1.5 rounded-md shadow-lg">
                ¡Solo {currentStock}!
              </div>
            ) : null}
          </div>

          {/* Imagen */}
          <ProductImage
            src={getImageUrl(product)}
            alt={product.name}
            size={400}
            fit="contain"
            className={`relative w-full h-full object-contain transform transition-all duration-500 ${isHovered ? 'scale-110' : 'scale-100'
              }`}
            data-testid="product-image"
          />
        </div>

        {/* Información del producto */}
        <div className="p-4 space-y-3">
          {/* Categoría y marca */}
          <div className="flex items-center justify-between gap-2">
            {product.category ? (
              <span className="text-xs text-blue-600 font-medium hover:underline">
                {product.category.name}
              </span>
            ) : null}
            {product.brand ? (
              <span className="text-xs text-gray-500">
                {product.brand}
              </span>
            ) : null}
          </div>

          {/* Nombre del producto */}
          <h3 className="text-sm font-medium text-gray-900 line-clamp-2 leading-snug min-h-[2.5rem]" data-testid="product-name">
            {product.name}
          </h3>

          {/* Packaging info */}
          {selectedVariant?.packaging?.presentation && (
            <div className="flex flex-wrap gap-1">
              <span className="inline-flex items-center text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                {selectedVariant.packaging.presentation}
              </span>
              {(selectedVariant.packaging.minimumQuantity || 1) > 1 && (
                <span className="inline-flex items-center text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded">
                  Mín. {selectedVariant.packaging.minimumQuantity} uds
                </span>
              )}
            </div>
          )}

          {/* Selector de variantes */}
          {hasVariants ? (
            <div onClick={(e) => e.preventDefault()}>
              <label className="text-xs font-medium text-gray-700 block mb-1">
                Tamaño:
              </label>
              <select
                value={selectedVariant?.sku || ''}
                onChange={(e) => {
                  e.stopPropagation();
                  const variant = product.variants?.find(v => v.sku === e.target.value);
                  setSelectedVariant(variant || null);
                }}
                className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all cursor-pointer"
              >
                {product.variants!.map((variant) => (
                  <option key={variant.sku} value={variant.sku}>
                    {variant.name || variant.attributes?.size || variant.attributes?.packaging}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {/* Precio */}
          <div className="pt-2 border-t border-gray-100">
            {!isAuthenticated ? (
              <div className="text-center py-1">
                <p className="text-sm text-gray-600 font-medium">
                  Inicia sesión para ver precio
                </p>
              </div>
            ) : currentPrice && currentPrice > 0 ? (
              <div className="space-y-1">
                {/* Precio con descuento */}
                {hasDiscount ? (
                  <div className="flex items-baseline gap-2">
                    <span className="text-xs text-gray-500">Antes:</span>
                    <span className="text-sm text-gray-400 line-through">
                      {currentOriginalPrice?.toFixed(2)}€
                    </span>
                  </div>
                ) : null}

                {/* Precio actual */}
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-gray-900" data-testid="product-price">
                    {currentPrice.toFixed(2)}€
                  </span>
                  {hasDiscount ? (
                    <span className="text-xs font-medium text-red-600">
                      Ahorra {(currentOriginalPrice! - currentPrice).toFixed(2)}€
                    </span>
                  ) : null}
                </div>

              </div>
            ) : (
              <p className="text-lg font-bold text-gray-900" data-testid="product-price">
                Consultar precio
              </p>
            )}
          </div>

          {/* Stock disponible - solo visible para usuarios autenticados */}
          {isAuthenticated && currentStock > 0 ? (
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full" />
              <span className="text-sm text-green-700 font-medium">
                En stock
              </span>
            </div>
          ) : null}

          {/* Badge de envío rápido - solo visible para usuarios autenticados */}
          {isAuthenticated && currentStock > 10 ? (
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span>Envío rápido disponible</span>
            </div>
          ) : null}
        </div>
      </Link>

      {/* Botón flotante de ver producto (visible en hover) */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: isHovered && (!isAuthenticated || currentStock > 0) ? 1 : 0, y: isHovered && (!isAuthenticated || currentStock > 0) ? 0 : 10 }}
        transition={{ duration: 0.2 }}
        className="absolute bottom-4 left-4 right-4 pointer-events-none"
      >
        <button
          onClick={(e) => {
            e.preventDefault();
            onProductClick(product, selectedVariant, index + 1);
            navigate(`/products/${product.id}`);
          }}
          className="w-full bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-semibold py-3 px-4 rounded-lg shadow-lg transition-colors pointer-events-auto flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          Ver producto
        </button>
      </motion.div>
    </motion.div>
  );
};

export default ProductCard;
